/**
 * Admin Dashboard Service
 * -----------------------
 * API calls for admin dashboard diagnostics (system health, matching).
 */

import { api, type ApiResponse } from '../utils';

// Types for system health
export interface ComponentHealth {
  status: 'healthy' | 'warning' | 'error';
  detail: string;
}

export interface CategoryHealth {
  status: 'healthy' | 'warning' | 'error';
  components: Record<string, ComponentHealth>;
}

export interface SystemHealthResponse {
  timestamp: string;
  overall_status: 'healthy' | 'warning' | 'error';
  ai_service: CategoryHealth;
  onboarding: CategoryHealth;
  matching: CategoryHealth;
  issues: string[];
}

// Types for matching diagnostics
export interface MultiVectorDetail {
  req: boolean;
  off: boolean;
}

export interface EmbeddingsInfo {
  count: number;
  types: string[];
  has_basic: boolean;
  multi_vector_complete: number;
  multi_vector_total: number;
  multi_vector_details: Record<string, MultiVectorDetail>;
}

export interface IntentClassification {
  inferred_intent: string;
  archetype: string | null;
  user_type: string | null;
}

export interface TemporalWeighting {
  days_inactive: number;
  activity_boost: number;
  boost_reason: string;
}

export interface SameObjectiveBlocking {
  enabled: boolean;
  intent: string;
  would_block: string[];
}

export interface MatchInfo {
  backend_count: number;
  backend_matches: any[];
  dynamo_matches: any[];
  bidirectional_scores: any[];
}

export interface PersonaInfo {
  requirements_preview: string;
  offerings_preview: string;
  focus: string | null;
}

export interface UserMatchingDiagnostics {
  user_id: string;
  email: string;
  name: string | null;
  onboarding_status: string;
  created_at: string | null;
  last_active_at: string | null;
  embeddings: EmbeddingsInfo;
  intent_classification: IntentClassification;
  dealbreakers: string[];
  temporal_weighting: TemporalWeighting;
  same_objective_blocking: SameObjectiveBlocking;
  matches: MatchInfo;
  persona: PersonaInfo;
}

export interface MatchingDiagnosticsResponse {
  result: UserMatchingDiagnostics[];
  summary: {
    total_users: number;
    with_multi_vector: number;
    with_basic_embeddings: number;
    with_matches: number;
    intent_breakdown: Record<string, number>;
  };
}

/**
 * Get system health status
 */
export const getSystemHealth = async (): Promise<ApiResponse<SystemHealthResponse>> => {
  try {
    const response = await api.get('/dashboard/admin/system-health');
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to get system health',
      result: null as any,
    };
  }
};

/**
 * Get matching diagnostics for all users
 */
export const getMatchingDiagnostics = async (): Promise<ApiResponse<MatchingDiagnosticsResponse>> => {
  try {
    const response = await api.get('/dashboard/admin/matching-diagnostics');
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to get matching diagnostics',
      result: null as any,
    };
  }
};

/**
 * Get admin user list
 */
export const getAdminUserList = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/dashboard/admin/user-list');
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to get user list',
      result: null as any,
    };
  }
};

// Types for wiring audit (truth-based health check)
export interface PipelineCheck {
  status: 'HEALTHY' | 'PARTIAL' | 'WARNING' | 'BROKEN' | 'ERROR';
  color: 'GREEN' | 'YELLOW' | 'RED';
  [key: string]: any;
}

export interface WiringAuditResponse {
  timestamp: string;
  latest_user: {
    id: string;
    email: string;
    name: string;
    onboarding_status: string;
    created_at: string;
    last_active_at?: string;
  } | null;
  pipeline_status: {
    basic_embeddings?: PipelineCheck;
    multi_vector_embeddings?: PipelineCheck;
    intent_classification?: PipelineCheck;
    matches?: PipelineCheck;
    matching_algorithm?: PipelineCheck;
  };
  overall_status: 'HEALTHY' | 'PARTIAL' | 'BROKEN' | 'ERROR' | 'NO_USERS' | 'UNKNOWN';
  color: 'GREEN' | 'YELLOW' | 'RED';
  efficiency: number;
  summary: {
    total_checks: number;
    healthy: number;
    partial: number;
    broken: number;
  };
  issues: string[];
  totals: {
    completed_users: number;
    total_matches: number;
    broken_users_count?: number;
  };
  broken_users?: {
    id: string;
    email: string;
    name: string | null;
    created_at: string;
    issue: string;
  }[];
}

/**
 * Get wiring audit - truth-based health check based on latest user journey
 * This replaces the old system-health which only checked if code exists
 */
export const getWiringAudit = async (): Promise<ApiResponse<WiringAuditResponse>> => {
  try {
    const response = await api.get('/dashboard/admin/wiring-audit');
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to get wiring audit',
      result: null as any,
    };
  }
};
